import { useMemo, useState } from "react"
import { useQuery } from "@rocicorp/zero/react"
import {
	IconCheckOutline18 as Check,
	IconChevronDownOutline18 as ChevronDown,
} from "nucleo-ui-outline-18"
import {
	Bar,
	BarChart,
	CartesianGrid,
	Line,
	LineChart,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts"

import {
	Button,
	DropdownMenu,
	DropdownMenuItem,
	useDarkMode,
} from "@productlane/ui"
import { queries } from "@productlane/zero/queries"

import { api } from "@/lib/api"
import { SettingsContainer } from "../../../settings-switches/settings-container"
import { SettingsSection } from "../../../settings-switches/settings-section"

const WINDOWS: { label: string; days: number }[] = [
	{ label: "Last 24 hours", days: 1 },
	{ label: "Last 7 days", days: 7 },
	{ label: "Last 14 days", days: 14 },
]
const DEFAULT_DAYS = 14

// Palette mirrors the workflow-state swatches used elsewhere in the product (Triage orange, Backlog/Todo grey, In Progress mustard, Review green, plus the P95 red reference line). Light + dark variants are tuned independently so neither mode feels washed out.
interface AnalyticsPalette {
	success: string
	successHover: string
	failure: string
	failureHover: string
	dead: string
	deadHover: string
	pending: string
	pendingHover: string
	apiLine: string
	webhookLine: string
	endpointColors: string[]
	endpointHovers: string[]
	other: string
	otherHover: string
}

const LIGHT_PALETTE: AnalyticsPalette = {
	success: "#7BC79A",
	successHover: "#9BD5B3",
	failure: "#DC5A4D",
	failureHover: "#E47A6E",
	dead: "#9B3A2D",
	deadHover: "#B85447",
	pending: "#9CA3AF",
	pendingHover: "#B5BCC4",
	apiLine: "#7BC79A",
	webhookLine: "#4A7BC7",
	endpointColors: ["#F26B47", "#E6B450", "#7BC79A", "#9CA3AF", "#1A1A1A"],
	endpointHovers: ["#F58B6E", "#EFC476", "#9BD5B3", "#B5BCC4", "#3A3A3A"],
	other: "#D1D5DB",
	otherHover: "#E5E7EB",
}

const DARK_PALETTE: AnalyticsPalette = {
	success: "#5FAA7D",
	successHover: "#7DBE96",
	failure: "#D45046",
	failureHover: "#DF7166",
	dead: "#9C342A",
	deadHover: "#B84A40",
	pending: "#A8A8A8",
	pendingHover: "#BEBEBE",
	apiLine: "#5FAA7D",
	webhookLine: "#5E8FD9",
	endpointColors: ["#D9543A", "#D9A347", "#5FAA7D", "#A8A8A8", "#FFFFFF"],
	endpointHovers: ["#E3735C", "#E2B463", "#7DBE96", "#BEBEBE", "#E5E7EB"],
	other: "#4B5563",
	otherHover: "#6B7280",
}

function usePalette(): AnalyticsPalette {
	const { isDarkMode } = useDarkMode()
	return isDarkMode ? DARK_PALETTE : LIGHT_PALETTE
}

export function ApiAnalyticsBlock() {
	const [days, setDays] = useState(DEFAULT_DAYS)
	const [keys] = useQuery(queries.apiKeysV2())
	const [hooks] = useQuery(queries.webhooks())

	const hasActiveKey = keys.some((k) => !k.revokedAt)
	if (!hasActiveKey && hooks.length === 0) return null

	const selectedWindow =
		WINDOWS.find((w) => w.days === days) ?? WINDOWS[WINDOWS.length - 1]!

	return (
		<>
			<SettingsSection
				title="API analytics"
				description="Usage and errors across all keys and webhooks in this workspace."
			/>
			<SettingsContainer className="mb-0 mt-4 divide-y px-0 pb-0 pt-0">
				<ApiRequestsCard
					days={days}
					headerAction={
						<DropdownMenu
							contentProps={{ side: "bottom", align: "end" }}
							trigger={
								<Button variant="outline" size="sm">
									{selectedWindow.label}
									<ChevronDown className="ml-1 h-3.5 w-3.5" />
								</Button>
							}
						>
							{WINDOWS.map((w) => (
								<DropdownMenuItem key={w.days} onClick={() => setDays(w.days)}>
									<div className="flex w-full items-center justify-between">
										{w.label}
										{w.days === days ? (
											<Check className="h-4 w-4" />
										) : undefined}
									</div>
								</DropdownMenuItem>
							))}
						</DropdownMenu>
					}
				/>
				<ApiErrorsByEndpointCard days={days} />
				<WebhookDeliveriesCard days={days} />
				<ResponseTimeCard days={days} />
			</SettingsContainer>
		</>
	)
}

// ---------- Shared chart shell + helpers ----------

interface LegendItem {
	label: string
	color: string
}

function ChartCard({
	title,
	legend,
	headerAction,
	children,
}: {
	title: string
	legend: LegendItem[]
	headerAction?: React.ReactNode
	children: React.ReactNode
}) {
	return (
		<div className="px-6 pb-4 pt-4">
			<div className="mb-3 flex items-start justify-between gap-4">
				<div className="flex flex-col items-start gap-1">
					<div className="font-mono text-sm text-gray-900 dark:text-white">
						{title}
					</div>
					<div className="flex flex-wrap items-center gap-x-5 gap-y-2">
						{legend.map((item) => (
							<div key={item.label} className="flex items-center gap-2">
								<div
									className="h-2.5 w-2.5 rounded-sm"
									style={{ backgroundColor: item.color }}
								/>
								<span className="text-xs text-gray-600 dark:text-gray-400">
									{item.label}
								</span>
							</div>
						))}
					</div>
				</div>
				{headerAction && <div className="shrink-0">{headerAction}</div>}
			</div>
			<div className="h-36 w-full [&_*]:outline-none">{children}</div>
		</div>
	)
}

// Recharts sets `color` to the series' true display color (Bar=fill, Line=stroke); prefer that.
// `fill` is unreliable as a fallback because Recharts uses "#fff" for the line dot's fill in dark mode.
function resolveSwatchColor(p: {
	fill?: string
	stroke?: string
	color?: string
}): string {
	const candidates = [p.color, p.stroke, p.fill]
	for (const c of candidates) {
		if (c && c !== "none" && c !== "#fff" && c !== "#ffffff") return c
	}
	return "#9ca3af"
}

// Reusable tooltip showing the stacked breakdown for a day; matches `ConversationMetrics`.
function StackedTooltip({
	active,
	payload,
	label,
	formatValue,
	showTotal = true,
}: {
	active?: boolean
	payload?: Array<{
		value: number
		name?: string
		dataKey?: string
		// Bars expose color on `fill`; lines on `stroke`. Either way we want the series color for the swatch.
		fill?: string
		stroke?: string
		color?: string
	}>
	label?: string
	formatValue?: (v: number) => string
	showTotal?: boolean
}) {
	if (!active || !payload || payload.length === 0) return null
	const total = payload.reduce((sum, p) => sum + (p.value ?? 0), 0)
	const fmt = formatValue ?? ((v: number) => v.toLocaleString())
	const heading = typeof label === "string" ? formatShortDate(label) : label
	return (
		<div className="rounded-lg border border-gray-200 bg-white p-3 shadow-lg dark:border-gray-700 dark:bg-gray-800">
			<p className="mb-2 text-sm font-medium text-gray-900 dark:text-white">
				{heading}
			</p>
			<div className="space-y-1">
				{payload.map((p) => (
					<div
						key={`${p.dataKey ?? p.name}`}
						className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400"
					>
						<div
							className="h-2.5 w-2.5 rounded-sm"
							style={{
								backgroundColor: resolveSwatchColor(p),
							}}
						/>
						<span className="truncate">{p.name ?? p.dataKey}:</span>
						<span className="ml-auto tabular-nums">{fmt(p.value ?? 0)}</span>
					</div>
				))}
				{showTotal && (
					<p className="border-gray-200 pt-1 text-sm font-medium text-gray-900 dark:border-gray-700 dark:text-white">
						Total: {fmt(total)}
					</p>
				)}
			</div>
		</div>
	)
}

// Stripe-style compact number formatter; 1500 → 1.5K, 1_500_000 → 1.5M. Leaves small numbers untouched.
function compactNumber(value: number): string {
	const abs = Math.abs(value)
	if (abs >= 1_000_000) return `${stripZero(value / 1_000_000)}M`
	if (abs >= 1_000) return `${stripZero(value / 1_000)}K`
	return `${value}`
}

function stripZero(value: number): string {
	const fixed = value.toFixed(1)
	return fixed.endsWith(".0") ? fixed.slice(0, -2) : fixed
}

function StyledAxes({
	data,
	tickFormatter,
}: {
	data: Array<{ day: string }>
	tickFormatter?: (v: number) => string
}) {
	// Show only the first and last date on the X-axis (Stripe-style); empty string skips the tick.
	const ticks = useMemo(() => {
		if (data.length === 0) return []
		if (data.length === 1) return [data[0]!.day]
		return [data[0]!.day, data[data.length - 1]!.day]
	}, [data])
	const formatDay = (value: string) => formatShortDate(value)
	const yFormatter = tickFormatter ?? compactNumber

	return (
		<>
			<CartesianGrid
				className="stroke-gray-200 dark:stroke-gray-700"
				vertical={false}
			/>
			<XAxis
				dataKey="day"
				ticks={ticks}
				tickFormatter={formatDay}
				tick={{ fontSize: 10, fill: "currentColor" }}
				className="text-gray-500 dark:text-gray-400"
				tickLine={false}
				axisLine={false}
				interval="preserveStartEnd"
				minTickGap={0}
			/>
			<YAxis
				orientation="right"
				tickFormatter={yFormatter}
				tick={{ fontSize: 10, fill: "currentColor" }}
				className="text-gray-500 dark:text-gray-400"
				axisLine={false}
				tickLine={false}
				width={32}
			/>
		</>
	)
}

// "2026-05-13" → "13 May"; "2026-05-13T14:00" → "14:00"; otherwise pass-through.
function formatShortDate(value: string): string {
	const hourMatch = /^\d{4}-\d{2}-\d{2}T(\d{2}):\d{2}$/.exec(value)
	if (hourMatch) return `${hourMatch[1]}:00`
	const dayMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
	if (!dayMatch) return value
	const [, , monthStr, dayStr] = dayMatch
	const months = [
		"Jan",
		"Feb",
		"Mar",
		"Apr",
		"May",
		"Jun",
		"Jul",
		"Aug",
		"Sep",
		"Oct",
		"Nov",
		"Dec",
	]
	const month = months[Number(monthStr) - 1] ?? monthStr
	return `${Number(dayStr)} ${month}`
}

// ---------- Individual chart cards ----------

function ApiRequestsCard({
	days,
	headerAction,
}: {
	days: number
	headerAction?: React.ReactNode
}) {
	const palette = usePalette()
	const query = api.apiAnalytics.workspaceRequestSeries.useQuery(
		{ days },
		{ keepPreviousData: true },
	)
	const data = query.data ?? []
	const hasData = data.some((row) => row.total > 0)

	return (
		<ChartCard
			title="API requests"
			legend={[
				{ label: "Successful", color: palette.success },
				{ label: "Failed", color: palette.failure },
			]}
			headerAction={headerAction}
		>
			{hasData ? (
				<ResponsiveContainer width="100%" height="100%">
					<BarChart data={data} barCategoryGap={4}>
						<StyledAxes data={data} />
						<Tooltip
							content={<StackedTooltip />}
							cursor={{ fill: "rgba(0, 0, 0, 0.05)" }}
						/>
						<Bar
							dataKey="successful"
							name="Successful"
							stackId="a"
							fill={palette.success}
							activeBar={{ fill: palette.successHover }}
							maxBarSize={12}
						/>
						<Bar
							dataKey="failed"
							name="Failed"
							stackId="a"
							fill={palette.failure}
							activeBar={{ fill: palette.failureHover }}
							maxBarSize={12}
						/>
					</BarChart>
				</ResponsiveContainer>
			) : (
				<div className="flex h-full items-center justify-center text-xs text-secondary">
					No requests in this window.
				</div>
			)}
		</ChartCard>
	)
}

function ApiErrorsByEndpointCard({ days }: { days: number }) {
	const palette = usePalette()
	const query = api.apiAnalytics.workspaceErrorsByEndpoint.useQuery(
		{ days },
		{ keepPreviousData: true },
	)
	const data = query.data?.series ?? []
	const endpoints = query.data?.endpoints ?? []
	const hasOther = query.data?.hasOther ?? false
	if (!query.data || data.length === 0) return null

	const legend: LegendItem[] = endpoints.map((label, idx) => ({
		label,
		color: palette.endpointColors[idx] ?? palette.other,
	}))
	if (hasOther) legend.push({ label: "other", color: palette.other })

	return (
		<ChartCard title="API error distribution" legend={legend}>
			<ResponsiveContainer width="100%" height="100%">
				<BarChart data={data} barCategoryGap={4}>
					<StyledAxes data={data} />
					<Tooltip
						content={<StackedTooltip />}
						cursor={{ fill: "rgba(0, 0, 0, 0.05)" }}
					/>
					{endpoints.map((endpointKey, idx) => {
						const restFill = palette.endpointColors[idx] ?? palette.other
						const hoverFill = palette.endpointHovers[idx] ?? palette.otherHover
						return (
							<Bar
								key={endpointKey}
								dataKey={endpointKey}
								name={endpointKey}
								stackId="a"
								fill={restFill}
								activeBar={{ fill: hoverFill }}
								maxBarSize={12}
							/>
						)
					})}
					{hasOther && (
						<Bar
							dataKey="other"
							name="other"
							stackId="a"
							fill={palette.other}
							activeBar={{ fill: palette.otherHover }}
							maxBarSize={12}
						/>
					)}
				</BarChart>
			</ResponsiveContainer>
		</ChartCard>
	)
}

function WebhookDeliveriesCard({ days }: { days: number }) {
	const palette = usePalette()
	const query = api.apiAnalytics.workspaceWebhookDeliveries.useQuery(
		{ days },
		{ keepPreviousData: true },
	)
	const data = query.data ?? []
	const hasData = data.some(
		(row) => row.succeeded + row.failed + row.dead + row.pending > 0,
	)
	if (!query.data || !hasData) return null

	const hasFailed = data.some((row) => row.failed > 0)
	const hasDead = data.some((row) => row.dead > 0)
	const hasPending = data.some((row) => row.pending > 0)

	const legend: LegendItem[] = [{ label: "Succeeded", color: palette.success }]
	if (hasFailed) legend.push({ label: "Failed", color: palette.failure })
	if (hasDead) legend.push({ label: "Dead", color: palette.dead })
	if (hasPending) legend.push({ label: "Pending", color: palette.pending })

	return (
		<ChartCard title="Event deliveries" legend={legend}>
			<ResponsiveContainer width="100%" height="100%">
				<BarChart data={data} barCategoryGap={4}>
					<StyledAxes data={data} />
					<Tooltip
						content={<StackedTooltip />}
						cursor={{ fill: "rgba(0, 0, 0, 0.05)" }}
					/>
					<Bar
						dataKey="succeeded"
						name="Succeeded"
						stackId="a"
						fill={palette.success}
						activeBar={{ fill: palette.successHover }}
						maxBarSize={12}
					/>
					{hasFailed && (
						<Bar
							dataKey="failed"
							name="Failed"
							stackId="a"
							fill={palette.failure}
							activeBar={{ fill: palette.failureHover }}
							maxBarSize={12}
						/>
					)}
					{hasDead && (
						<Bar
							dataKey="dead"
							name="Dead"
							stackId="a"
							fill={palette.dead}
							activeBar={{ fill: palette.deadHover }}
							maxBarSize={12}
						/>
					)}
					{hasPending && (
						<Bar
							dataKey="pending"
							name="Pending"
							stackId="a"
							fill={palette.pending}
							activeBar={{ fill: palette.pendingHover }}
							maxBarSize={12}
						/>
					)}
				</BarChart>
			</ResponsiveContainer>
		</ChartCard>
	)
}

function ResponseTimeCard({ days }: { days: number }) {
	const palette = usePalette()
	const query = api.apiAnalytics.workspaceLatencySeries.useQuery(
		{ days },
		{ keepPreviousData: true },
	)
	const data = query.data ?? []
	const hasData = data.some((row) => row.apiP95 > 0 || row.webhookP95 > 0)
	if (!query.data || !hasData) return null

	return (
		<ChartCard
			title="Response time"
			legend={[
				{ label: "API requests", color: palette.apiLine },
				{ label: "Webhook deliveries", color: palette.webhookLine },
			]}
		>
			<ResponsiveContainer width="100%" height="100%">
				<LineChart data={data}>
					<StyledAxes data={data} tickFormatter={(v) => `${v}`} />
					<Tooltip
						content={
							<StackedTooltip
								formatValue={(v) => `${v.toLocaleString()} ms`}
								showTotal={false}
							/>
						}
						cursor={{ stroke: "rgba(0, 0, 0, 0.08)", strokeWidth: 1 }}
					/>
					<Line
						type="monotone"
						dataKey="apiP95"
						name="API"
						stroke={palette.apiLine}
						strokeWidth={2}
						dot={false}
					/>
					<Line
						type="monotone"
						dataKey="webhookP95"
						name="Webhook"
						stroke={palette.webhookLine}
						strokeWidth={2}
						dot={false}
					/>
				</LineChart>
			</ResponsiveContainer>
		</ChartCard>
	)
}
